(function () {
  "use strict";

  const VID_KEY = "sa_private_edit_vid";
  const PROFILE_KEY = "sa_private_edit_profile_v5";
  const RECENT_KEY = "recently_viewed";
  const MAX_SELECTED = 4;
  const MAX_RECENT = 6;
  const EVENT_ENDPOINT = "/ai/event";
  const AI_ENDPOINT = "/ai/private-edit";

  const COLOR_WORDS = [
    "black","white","red","pink","blue","purple","green","brown","beige",
    "cream","ivory","nude","gold","silver","magenta","orchid","champagne",
    "noir","rouge","blush","navy","wine","burgundy"
  ];

  const MOOD_MAP = {
    lace: "romantic detail",
    satin: "soft polish",
    corset: "structured confidence",
    garter: "after-dark styling",
    bodysuit: "sculpted ease",
    teddy: "one-piece confidence",
    harness: "bold structure",
    babydoll: "soft romance",
    mesh: "sheer texture",
    roleplay: "playful escape",
    sheer: "sheer texture",
    leather: "bold structure",
    velvet: "soft polish"
  };

  const STOP = new Set([
    "the","and","with","for","set","sets","lingerie","stylinarts","women",
    "woman","sexy","luxury","shop","edit","collection","your","you","from",
    "this","that","piece","pieces","bra","brief","panty","panties","product",
    "new","sale","style","styles"
  ]);

  const state = {
    viewStart: Date.now(),
    maxScrollDepth: 0,
    scrollSent: {},
    galleryClicks: 0,
    hoverTimers: new WeakMap(),
    hoverStart: new WeakMap()
  };

  function safeJson(raw, fallback) {
    try { return JSON.parse(raw) || fallback; }
    catch { return fallback; }
  }

  function clean(v) {
    return String(v || "").toLowerCase().trim();
  }

  function words(v) {
    return clean(v)
      .replace(/[^a-z0-9\s-]/g, " ")
      .split(/[\s-]+/)
      .filter(w => w.length > 2 && !STOP.has(w));
  }

  function getVisitorId() {
    let id = localStorage.getItem(VID_KEY);
    if (!id) {
      if (window.crypto && crypto.randomUUID) {
        id = crypto.randomUUID();
      } else {
        id = "sa_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2);
      }
      localStorage.setItem(VID_KEY, id);
    }
    return id;
  }

  function getProductHandle() {
    const profile = document.querySelector("[data-sa-product-profile]");
    if (profile && profile.dataset.handle) return profile.dataset.handle;

    const explicit =
      document.querySelector("[data-product-handle]") ||
      document.querySelector("[data-handle]");

    if (explicit) {
      if (explicit.dataset.productHandle) return explicit.dataset.productHandle;
      if (explicit.dataset.handle) return explicit.dataset.handle;
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && canonical.href) {
      const m = canonical.href.match(/\/products\/([^/?#]+)/);
      if (m) return m[1];
    }

    const match = location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : "";
  }

  function getCardHandle(card) {
    if (!card) return "";

    if (card.dataset.handle) return card.dataset.handle;
    if (card.dataset.productHandle) return card.dataset.productHandle;

    const link = card.querySelector('a[href*="/products/"]');
    if (link) {
      const m = link.getAttribute("href").match(/\/products\/([^/?#]+)/);
      if (m) return m[1];
    }

    return "";
  }

  function sendEvent(eventType, handle, extra) {
    if (!eventType) return;

    const payload = {
      visitor_id: getVisitorId(),
      event_type: eventType,
      product_handle: handle || getProductHandle() || null,
      metadata_json: extra || null,
      extra: extra || null,
      timestamp: Date.now(),
      source_path: location.pathname
    };

    fetch(EVENT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify(payload)
    }).catch(function () {});
  }

  function getProfile() {
    return safeJson(localStorage.getItem(PROFILE_KEY), {
      views: [],
      terms: {},
      colors: {},
      moods: {},
      sizes: {},
      events: {
        views: 0,
        sizeGuide: 0,
        addToCart: 0,
        wishlist: 0,
        dwell30: 0,
        dwell60: 0,
        hoverShort: 0,
        hoverLong: 0,
        scroll75: 0,
        gallery: 0
      }
    });
  }

  function saveProfile(p) {
    p.updatedAt = new Date().toISOString();
    localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
  }

  function addCount(obj, key, weight) {
    key = clean(key);
    if (!key) return;
    obj[key] = (obj[key] || 0) + (weight || 1);
  }

  function addTerms(profile, value, weight) {
    words(value).forEach(w => {
      addCount(profile.terms, w, weight || 1);
      if (COLOR_WORDS.includes(w)) addCount(profile.colors, w, (weight || 1) + 1);
      if (MOOD_MAP[w]) addCount(profile.moods, MOOD_MAP[w], (weight || 1) + 2);
    });
  }

  function top(obj, limit) {
    return Object.entries(obj || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit || 3)
      .map(x => x[0]);
  }

  async function fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  function trackCurrentPdp() {
    const el = document.querySelector("[data-sa-product-profile]");
    const handle = getProductHandle();

    if (!handle) return;

    const lastKey = "sa_private_edit_last_view";
    const now = Date.now();

    try {
      const last = JSON.parse(localStorage.getItem(lastKey) || "{}");
      if (last.handle !== handle || now - last.time > 30000) {
        sendEvent("view", handle, {
          path: location.pathname,
          hour: new Date().getHours(),
          referrer: document.referrer || null
        });
      }
    } catch {
      sendEvent("view", handle, {
        path: location.pathname,
        hour: new Date().getHours(),
        referrer: document.referrer || null
      });
    }

    localStorage.setItem(lastKey, JSON.stringify({ handle: handle, time: now }));

    if (!el) return;

    const p = getProfile();

    const item = {
      handle: handle,
      title: el.dataset.title || "",
      url: el.dataset.url || `/products/${handle}`,
      image: el.dataset.image || "",
      tags: el.dataset.tags || "",
      time: now
    };

    p.views = p.views.filter(v => v.handle !== item.handle);
    p.views.unshift(item);
    p.views = p.views.slice(0, MAX_RECENT);

    addTerms(p, item.handle, 2);
    addTerms(p, item.title, 3);
    addTerms(p, item.tags, 2);

    p.events.views += 1;
    saveProfile(p);
  }

  async function hydrateFromRecentlyViewed() {
    const p = getProfile();
    const handles = safeJson(localStorage.getItem(RECENT_KEY), []).filter(Boolean).slice(0, MAX_RECENT);

    for (const handle of handles) {
      if (p.views.some(v => v.handle === handle)) continue;

      const data = await fetchProduct(handle);
      if (!data) continue;

      const image = Array.isArray(data.images) ? data.images[0] : data.featured_image;

      const item = {
        handle: data.handle || handle,
        title: data.title || handle,
        url: data.url || `/products/${handle}`,
        image: image || "",
        tags: Array.isArray(data.tags) ? data.tags.join(" ") : "",
        time: Date.now()
      };

      p.views.unshift(item);
      addTerms(p, item.handle, 2);
      addTerms(p, item.title, 3);
      addTerms(p, item.tags, 2);
      addTerms(p, data.product_type || "", 3);
    }

    p.views = p.views.slice(0, MAX_RECENT);
    p.events.views = Math.max(p.events.views, p.views.length);
    saveProfile(p);

    return p;
  }

  function trackClicksAndSize() {
    document.addEventListener("click", function (e) {
      const handle = getProductHandle();
      const p = getProfile();

      if (e.target.closest(".size-chart, [data-size-chart], .js-modal-open, [href*='size']")) {
        p.events.sizeGuide += 1;
        sendEvent("size_guide", handle);
      }

      if (e.target.closest("button[name='add'], [type='submit'][name='add'], #addToCart, #AddToCart")) {
        p.events.addToCart += 1;
        sendEvent("add_to_cart", handle);
      }

      if (e.target.closest("[data-sa-wishlist], .sa-wishlist-btn, .sa-wishlist-heart")) {
        p.events.wishlist += 1;
        sendEvent("wishlist", handle);
      }

      if (
        e.target.closest(".product-gallery") ||
        e.target.closest(".thumbnail") ||
        e.target.closest("[data-product-thumbnail]") ||
        e.target.closest(".product-single__thumbnail")
      ) {
        p.events.gallery += 1;
        state.galleryClicks += 1;
        sendEvent("gallery_interaction", handle, { clicks: state.galleryClicks });
      }

      saveProfile(p);
    });

    document.addEventListener("change", function (e) {
      const el = e.target;
      const name = clean(el.name || el.getAttribute("aria-label") || "");
      const value = clean(el.value || "");
      const handle = getProductHandle();

      if (!value) return;

      if (name.includes("size")) {
        const p = getProfile();
        addCount(p.sizes, value, 3);
        saveProfile(p);
        sendEvent("size_select", handle, { value: value, size: value });
      }

      if (name.includes("color") || name.includes("colour")) {
        sendEvent("color_select", handle, { value: value, color: value });
      }
    });
  }

  function trackDwellTime() {
    const handle = getProductHandle();
    if (!handle) return;

    let sent30 = false;
    let sent60 = false;
    let sent120 = false;

    const timer = setInterval(function () {
      const duration = Date.now() - state.viewStart;

      if (!sent30 && duration >= 30000) {
        sent30 = true;
        const p = getProfile();
        p.events.dwell30 = (p.events.dwell30 || 0) + 1;
        saveProfile(p);
        sendEvent("pdp_dwell_30", handle, { duration_ms: duration });
      }

      if (!sent60 && duration >= 60000) {
        sent60 = true;
        const p = getProfile();
        p.events.dwell60 = (p.events.dwell60 || 0) + 1;
        saveProfile(p);
        sendEvent("pdp_dwell_60", handle, { duration_ms: duration });
      }

      if (!sent120 && duration >= 120000) {
        sent120 = true;
        sendEvent("pdp_dwell_120", handle, { duration_ms: duration });
        clearInterval(timer);
      }
    }, 5000);

    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden") {
        const duration = Date.now() - state.viewStart;
        if (duration >= 8000) {
          sendEvent("pdp_exit_dwell", handle, { duration_ms: duration });
        }
      }
    });
  }

  function trackScrollDepth() {
    const handle = getProductHandle();
    if (!handle) return;

    window.addEventListener("scroll", function () {
      const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
      if (height <= 0) return;

      const depth = Math.max(0, Math.min(100, Math.round((window.scrollY / height) * 100)));
      state.maxScrollDepth = Math.max(state.maxScrollDepth, depth);

      [25, 50, 75, 90].forEach(function (mark) {
        if (depth >= mark && !state.scrollSent[mark]) {
          state.scrollSent[mark] = true;
          if (mark >= 75) {
            const p = getProfile();
            p.events.scroll75 = (p.events.scroll75 || 0) + 1;
            saveProfile(p);
          }
          sendEvent("scroll_" + mark, handle, { depth: depth });
        }
      });
    }, { passive: true });
  }

  function trackHoverIntent() {
    const selector = [
      "[data-sa-private-card]",
      "[data-product-card]",
      ".product-card",
      ".grid-product",
      ".collection-grid-item",
      ".product-index"
    ].join(",");

    document.addEventListener("mouseover", function (e) {
      const card = e.target.closest(selector);
      if (!card || state.hoverStart.has(card)) return;

      const handle = getCardHandle(card);
      if (!handle) return;

      state.hoverStart.set(card, Date.now());

      const timer = setTimeout(function () {
        sendEvent("hover_short", handle, { hover_ms: 1500, source_path: location.pathname });
        const p = getProfile();
        p.events.hoverShort = (p.events.hoverShort || 0) + 1;
        saveProfile(p);
      }, 1500);

      state.hoverTimers.set(card, timer);
    });

    document.addEventListener("mouseout", function (e) {
      const card = e.target.closest(selector);
      if (!card) return;

      const related = e.relatedTarget;
      if (related && card.contains(related)) return;

      const start = state.hoverStart.get(card);
      const timer = state.hoverTimers.get(card);

      if (timer) clearTimeout(timer);

      state.hoverStart.delete(card);
      state.hoverTimers.delete(card);

      if (!start) return;

      const duration = Date.now() - start;
      const handle = getCardHandle(card);
      if (!handle || duration < 1200) return;

      if (duration >= 3500) {
        const p = getProfile();
        p.events.hoverLong = (p.events.hoverLong || 0) + 1;
        saveProfile(p);
        sendEvent("hover_long", handle, { hover_ms: duration, source_path: location.pathname });
      }
    });
  }

  async function fetchPrivateEditAI() {
    try {
      const res = await fetch(`${AI_ENDPOINT}?visitor_id=${encodeURIComponent(getVisitorId())}`, {
        credentials: "same-origin",
        cache: "no-store"
      });
      return await res.json();
    } catch {
      return null;
    }
  }

  function sentenceList(arr) {
    if (!arr.length) return "";
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
    return `${arr[0]}, ${arr[1]} and ${arr[2]}`;
  }

  function updateCopyFromProfile(section, p) {
    const mood = top(p.moods, 2);
    const colors = top(p.colors, 2);
    const sizes = top(p.sizes, 1);

    const hero = section.querySelector("[data-sa-pe-hero]");
    const proof = section.querySelector("[data-sa-pe-proof]");
    const fit = section.querySelector("[data-sa-pe-fit]");
    const intent = section.querySelector("[data-sa-pe-intent]");
    const emotion = section.querySelector("[data-sa-pe-emotion]");

    if (hero) {
      const moodText = mood.length ? sentenceList(mood) : "considered, after-dark pieces";
      const colorText = colors.length ? ` in ${sentenceList(colors)}` : "";
      hero.textContent = `Your edit is leaning toward ${moodText}${colorText}.`;
    }

    if (emotion) {
      emotion.textContent = "A private read of what you paused on, returned to, and considered more than once.";
    }

    if (proof) {
      const parts = [];
      if (p.events.views) parts.push(`${p.events.views} viewed`);
      if (p.events.dwell30) parts.push("longer product reading");
      if (p.events.hoverLong) parts.push("hover hesitation");
      if (p.events.sizeGuide) parts.push(`${p.events.sizeGuide} sizing check${p.events.sizeGuide > 1 ? "s" : ""}`);
      if (p.events.wishlist) parts.push(`${p.events.wishlist} wishlist signal${p.events.wishlist > 1 ? "s" : ""}`);
      if (p.events.addToCart) parts.push("cart intent");
      proof.textContent = parts.length ? parts.join(" · ") : "Built from what you viewed and returned to.";
    }

    if (fit) {
      fit.textContent = sizes.length
        ? `Size signal: you last interacted with ${sizes[0].toUpperCase()}.`
        : p.events.sizeGuide
          ? "Fit signal: you checked sizing, so this edit favours easier, more adjustable pieces."
          : "Fit signal: still learning your preferred size and fit.";
    }

    if (intent) {
      intent.textContent = p.events.addToCart || p.events.wishlist
        ? "Intent signal: you moved beyond browsing, so this edit is tighter and more selective."
        : p.events.hoverLong || p.events.dwell30
          ? "Intent signal: you paused long enough for the edit to sharpen around this mood."
          : "Intent signal: you are still exploring, so this edit stays broad and discovery-led.";
    }
  }

function updateCopyFromAI(section, data, p) {
    const hero = section.querySelector("[data-sa-pe-hero]");
    const proof = section.querySelector("[data-sa-pe-proof]");
    const fit = section.querySelector("[data-sa-pe-fit]");
    const intent = section.querySelector("[data-sa-pe-intent]");
    const emotion = section.querySelector("[data-sa-pe-emotion]");
    const fmTitle = section.querySelector("[data-sa-pe-future-title]");
    const fmLine = section.querySelector("[data-sa-pe-future-line]");
    const fmMood = section.querySelector("[data-sa-pe-future-mood]");
    const confidence = section.querySelector("[data-sa-pe-confidence]");
    const cinematic = section.querySelector("[data-sa-pe-cinematic]");
    const nextDesire = section.querySelector("[data-sa-pe-next-desire]");
    const timeline = section.querySelector("[data-sa-pe-timeline]");
    const identity = section.querySelector("[data-sa-pe-identity]");

    const phase10 = data.phase10_narrative || data.editorial_narrative || {};
    const story = data.editorial_story || data.phase8_editorial || {};
    const atmosphere = data.atmosphere || story.atmosphere || {};

    if (hero) hero.textContent = phase10.modules?.style_direction || phase10.headline || story.headline || data.insight || "Your edit is learning what you return to.";
    if (emotion) emotion.textContent = phase10.evolution_summary || story.stylist_note || data.future_memory || data.future_memory_line || "A private read of the pieces your behaviour keeps pulling forward.";
    if (proof) proof.textContent = phase10.modules?.stylist_read || phase10.story || story.why || data.reason || "AI profile active from your browsing pattern.";

    if (fit) {
      const ig = data.identity_graph || phase10.identity || {};
      fit.textContent = ig.silhouette_confidence || data.fit_read || "Still learning your preferred size and fit.";
    }

    if (intent) intent.textContent = phase10.modules?.next_pull || phase10.next_move || story.next_desire_line || data.intent_read || "Discovery-led until stronger buying signals appear.";

    if (timeline) {
      timeline.textContent = phase10.modules?.memory_timeline || phase10.timeline_story || data.timeline_story || "";
      timeline.closest("[data-sa-pe-timeline-wrap]")?.toggleAttribute("hidden", !timeline.textContent.trim());
    }

    if (identity) {
      const ig = data.identity_graph || phase10.identity || {};
      identity.textContent = ig.style_maturity ? `${ig.style_maturity} · ${ig.risk_appetite || "forming"}` : "";
      identity.closest("[data-sa-pe-identity-wrap]")?.toggleAttribute("hidden", !identity.textContent.trim());
    }

    if (cinematic) cinematic.textContent = story.cinematic_caption || data.cinematic_caption || phase10.evolution_summary || "Chosen from your real browsing pattern.";

    if (nextDesire) {
      nextDesire.textContent = data.next_desire || phase10.next_move || story.next_desire_line || "";
      nextDesire.closest("[data-sa-pe-next-wrap]")?.toggleAttribute("hidden", !nextDesire.textContent.trim());
    }

    if (fmTitle) fmTitle.textContent = data.future_memory_title || "The Moment After";
    if (fmLine) fmLine.textContent = phase10.next_move || data.future_memory_line || data.future_memory || "A private selection for the version of you that keeps returning.";

    if (fmMood) {
      const mode = data.visual_mode || story.visual_mode || data.state || "private edit";
      fmMood.textContent = String(mode).replace(/_/g, " ");
    }

    if (confidence && typeof data.confidence !== "undefined") confidence.textContent = `${data.confidence}% confidence`;

    if (atmosphere.tone) section.setAttribute("data-sa-pe-mood", atmosphere.tone);
    if (atmosphere.density) section.setAttribute("data-sa-pe-density", atmosphere.density);
    if (atmosphere.motion) section.setAttribute("data-sa-pe-motion", atmosphere.motion);
    if (atmosphere.typography) section.setAttribute("data-sa-pe-type", atmosphere.typography);
    if (atmosphere.composition) section.setAttribute("data-sa-pe-composition", atmosphere.composition);
  }

  function scoreCard(card, p) {
    const hay = clean(`${card.dataset.handle} ${card.dataset.title} ${card.dataset.tags}`);
    const terms = top(p.terms, 10);
    const colors = top(p.colors, 4);
    const moods = top(p.moods, 4);

    let score = 0;

    terms.forEach((t, i) => { if (hay.includes(t)) score += Math.max(10 - i, 2); });
    colors.forEach((c, i) => { if (hay.includes(c)) score += Math.max(8 - i, 2); });
    moods.forEach(m => {
      Object.entries(MOOD_MAP).forEach(([raw, label]) => {
        if (label === m && hay.includes(raw)) score += 6;
      });
    });

    if (p.views.some(v => v.handle === card.dataset.handle)) score -= 50;

    return score;
  }


function money(value) {
  if (value === null || typeof value === "undefined" || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `$${n.toFixed(2)}`;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function roleLabel(role, index) {
  if (role === "lead_piece" || index === 0) return "Lead piece";
  if (role === "mood_finisher" || index === 3) return "Mood finisher";
  if (role === "confidence_enhancer") return "Confidence enhancer";
  return "Supporting piece";
}





function phase6Escape(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderProductsFromAI(section, data) {
  const grid = section.querySelector("[data-sa-pe-grid]");
  const cards = Array.from(section.querySelectorAll("[data-sa-private-card]"));

  if (!grid || !data || !data.products || !data.products.length) return false;

  grid.innerHTML = "";

  data.products.slice(0, MAX_SELECTED).forEach((product, i) => {
    const handle = String(product.handle || "").trim();
    const role = product.role || (i === 0 ? "lead_piece" : i === 3 ? "mood_finisher" : "supporting_piece");
    let card = cards.find(c => c.dataset.handle === handle);

    if (card) {
      card.hidden = false;
      card.classList.add("sa-pe8-card");
      card.setAttribute("data-sa-role", role);
      card.setAttribute("data-sa-why", product.why || "");
      card.style.setProperty("--delay", `${i * 85}ms`);
      card.style.setProperty("--rank", String(i));

      const text = card.querySelector(".sa-pe8-card__text, .sa-pe7-card__text, .sa-pe6-card__text, .sa-pe5-card__text");

      if (text && !text.querySelector("[data-sa-card-role]")) {
        const roleEl = document.createElement("span");
        roleEl.setAttribute("data-sa-card-role", "");
        roleEl.className = "sa-pe8-card__role";
        roleEl.textContent = roleLabel(role, i);
        text.prepend(roleEl);
      } else if (text && text.querySelector("[data-sa-card-role]")) {
        text.querySelector("[data-sa-card-role]").textContent = roleLabel(role, i);
      }

      if (text && product.why && !text.querySelector("[data-sa-card-why]")) {
        const why = document.createElement("p");
        why.setAttribute("data-sa-card-why", "");
        why.className = "sa-pe8-card__why";
        why.textContent = product.why;
        text.appendChild(why);
      } else if (text && text.querySelector("[data-sa-card-why]")) {
        text.querySelector("[data-sa-card-why]").textContent = product.why || "";
      }

      grid.appendChild(card);
      return;
    }

    const article = document.createElement("article");
    article.className = "sa-pe8-card";
    article.setAttribute("data-sa-role", role);
    article.setAttribute("data-sa-why", product.why || "");
    article.style.setProperty("--delay", `${i * 85}ms`);
    article.style.setProperty("--rank", String(i));

    const url = product.url || `/products/${handle}`;
    const image = product.image || "";
    const title = product.title || handle.replace(/-/g, " ");
    const price = money(product.price);
    const why = product.why || "Chosen from your real browsing pattern.";

    article.innerHTML = `
      <a href="${phase6Escape(url)}" class="sa-pe8-card__media">
        ${image ? `<img src="${phase6Escape(image)}" alt="${phase6Escape(title)}" loading="lazy">` : ""}
      </a>
      <div class="sa-pe8-card__text">
        <span class="sa-pe8-card__role" data-sa-card-role>${phase6Escape(roleLabel(role, i))}</span>
        <a href="${phase6Escape(url)}">${phase6Escape(title)}</a>
        ${price ? `<p class="sa-pe8-card__price">${phase6Escape(price)}</p>` : ""}
        <p class="sa-pe8-card__why" data-sa-card-why>${phase6Escape(why)}</p>
      </div>
    `;

    grid.appendChild(article);
  });

  return grid.children.length > 0;
}


  function renderProductsFromProfile(section, p) {
    const grid = section.querySelector("[data-sa-pe-grid]");
    const cards = Array.from(section.querySelectorAll("[data-sa-private-card]"));

    if (!grid || !cards.length) return false;

    let selected = cards
      .map(card => ({ card, score: scoreCard(card, p) }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_SELECTED)
      .map(x => x.card);

    if (selected.length < MAX_SELECTED) {
      for (const card of cards) {
        if (selected.length >= MAX_SELECTED) break;
        if (!selected.includes(card)) selected.push(card);
      }
    }

    if (!selected.length) return false;

    grid.innerHTML = "";

    selected.forEach((card, i) => {
      card.hidden = false;
      card.style.setProperty("--delay", `${i * 90}ms`);
      grid.appendChild(card);
    });

    return true;
  }

  function renderRecent(section, p) {
    const rail = section.querySelector("[data-sa-pe-recent]");
    if (!rail) return;

    rail.innerHTML = "";

    p.views.slice(0, 4).forEach(item => {
      const a = document.createElement("a");
      a.href = item.url || `/products/${item.handle}`;
      a.className = "sa-pe8-recent";
      a.innerHTML = `
        ${item.image ? `<img src="${item.image}" alt="">` : ""}
        <span>${item.title || item.handle}</span>
      `;
      rail.appendChild(a);
    });
  }

  function whyToggle(section) {
    const btn = section.querySelector("[data-sa-pe-why-btn]");
    const panel = section.querySelector("[data-sa-pe-why-panel]");
    if (!btn || !panel || btn.dataset.bound === "1") return;

    btn.dataset.bound = "1";

    btn.addEventListener("click", function () {
      const open = panel.hasAttribute("hidden");
      panel.toggleAttribute("hidden", !open);
      btn.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }





function syncCustomerProfile() {
  const c = window.saPrivateEditCustomer;
  if (!c || !c.customer_id) return;

  const totalSpent = Number(c.total_spent || 0);
  const ordersCount = Number(c.orders_count || 0);

  fetch("/ai/customer-sync", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      visitor_id: getVisitorId(),
      customer_id: String(c.customer_id),
      first_name: c.first_name || null,
      orders_count: ordersCount,
      average_order_value: ordersCount > 0 ? totalSpent / ordersCount : 0
    })
  }).catch(function () {});
}














  function rewritePhase6StylistNotes(section) {
    const proof = section.querySelector("[data-sa-pe-proof]");
    const fit = section.querySelector("[data-sa-pe-fit]");
    const intent = section.querySelector("[data-sa-pe-intent]");
    const emotion = section.querySelector("[data-sa-pe-emotion]");

    if (proof) {
      const raw = proof.textContent || "";
      if (/product signal|repeat interest|hover hesitation|longer reading|account memory/i.test(raw)) {
        const parts = [];
        if (/repeat interest/i.test(raw)) parts.push("you came back to this mood more than once");
        if (/hover hesitation/i.test(raw)) parts.push("you paused before moving on");
        if (/longer reading/i.test(raw)) parts.push("you spent longer with these pieces");
        if (/account memory/i.test(raw)) parts.push("your account memory refined the edit");
        proof.textContent = parts.length ? parts.join("; ") + "." : "shaped by the pieces you keep returning to.";
      }
    }

    if (fit && /^Fit read:/i.test(fit.textContent || "")) {
      fit.textContent = fit.textContent.replace(/^Fit read:\s*/i, "");
      fit.textContent = fit.textContent.charAt(0).toUpperCase() + fit.textContent.slice(1);
    }

    if (intent && /^Intent read:/i.test(intent.textContent || "")) {
      intent.textContent = intent.textContent.replace(/^Intent read:\s*/i, "");
      intent.textContent = intent.textContent.charAt(0).toUpperCase() + intent.textContent.slice(1);
    }

    if (emotion && /The character changes first/i.test(emotion.textContent || "")) {
      emotion.textContent = "The edit is becoming playful, styled, and more deliberate.";
    }
  }

  function applyPhase6Mood(section) {
    const state = section.getAttribute("data-sa-private-edit-state") || "explorer";
    const confidence = Number(section.getAttribute("data-sa-private-edit-confidence") || 0);

    section.classList.toggle("sa-pe8--high-confidence", confidence >= 72);
    section.classList.toggle("sa-pe8--low-confidence", confidence > 0 && confidence < 45);

    let mood = "quiet";
    if (/dark|structured|shortlist/i.test(state)) mood = "structured";
    if (/playful|costume|roleplay/i.test(state)) mood = "playful";
    if (/fit/i.test(state)) mood = "fit";
    if (/romantic|soft/i.test(state)) mood = "romantic";

    section.setAttribute("data-sa-pe-mood", mood);
  }

  function applyPhase6Composition(section) {
    const cards = Array.from(section.querySelectorAll(".sa-pe8-card"));
    if (!cards.length) return;

    cards.forEach((card, index) => {
      const role = card.getAttribute("data-sa-role") || "";
      const isLead = index === 0 || role === "lead_piece";
      const isFinisher = index === 3 || role === "mood_finisher";

      card.classList.toggle("is-lead", isLead);
      card.classList.toggle("is-support", !isLead && !isFinisher);
      card.classList.toggle("is-finisher", isFinisher);
      card.style.setProperty("--pe-order", String(index));

      const media = card.querySelector(".sa-pe8-card__media");
      if (media && !media.querySelector(".sa-pe8-card__depth")) {
        const depth = document.createElement("span");
        depth.className = "sa-pe8-card__depth";
        media.appendChild(depth);
      }

      if (media && !media.querySelector(".sa-pe8-card__shine")) {
        const shine = document.createElement("span");
        shine.className = "sa-pe8-card__shine";
        media.appendChild(shine);
      }
    });
  }

  function bindPhase6Motion(section) {
    if (!section || section.dataset.phase6MotionBound === "1") return;
    section.dataset.phase6MotionBound = "1";

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    section.addEventListener("pointermove", function (event) {
      if (window.innerWidth < 990) return;

      const cards = section.querySelectorAll(".sa-pe8-card");
      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        ) return;

        const lead = card.classList.contains("is-lead");
        const strength = lead ? 9 : 5;
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * strength;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * strength;

        card.style.setProperty("--mx", `${x.toFixed(2)}px`);
        card.style.setProperty("--my", `${y.toFixed(2)}px`);
      });
    }, { passive: true });

    section.addEventListener("pointerleave", function () {
      section.querySelectorAll(".sa-pe8-card").forEach((card) => {
        card.style.setProperty("--mx", "0px");
        card.style.setProperty("--my", "0px");
      });
    }, { passive: true });
  }

  function bindPhase6RailPhysics(section) {
    const rails = [
      section.querySelector("[data-sa-pe-grid]"),
      section.querySelector(".sa-pe8__reads"),
      section.querySelector("[data-sa-pe-recent]")
    ].filter(Boolean);

    rails.forEach((rail) => {
      if (rail.dataset.phase6RailBound === "1") return;
      rail.dataset.phase6RailBound = "1";

      let raf = null;
      let lastLeft = rail.scrollLeft;
      let velocity = 0;

      const update = function () {
        raf = null;
        const max = Math.max(1, rail.scrollWidth - rail.clientWidth);
        const progress = Math.max(0, Math.min(1, rail.scrollLeft / max));
        velocity = rail.scrollLeft - lastLeft;
        lastLeft = rail.scrollLeft;

        if (rail.matches("[data-sa-pe-grid]")) {
          section.style.setProperty("--rail-progress", progress.toFixed(3));
          section.style.setProperty("--rail-velocity", Math.max(-1, Math.min(1, velocity / 22)).toFixed(3));
        }
      };

      rail.addEventListener("scroll", function () {
        if (!raf) raf = requestAnimationFrame(update);
      }, { passive: true });

      update();
    });
  }

  function bindPhase6Reveal(section) {
    if (!("IntersectionObserver" in window) || section.dataset.phase6RevealBound === "1") return;
    section.dataset.phase6RevealBound = "1";

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          section.classList.add("sa-pe8--in-view");
        }
      });
    }, { threshold: 0.2 });

    observer.observe(section);
  }

  function applyPhase6Luxury(section) {
    rewritePhase6StylistNotes(section);
    applyPhase6Mood(section);
    applyPhase6Composition(section);
    bindPhase6Motion(section);
    bindPhase6RailPhysics(section);
    bindPhase6Reveal(section);
  }



  function bindPhase8RailFocus(section) {
    const rail = section.querySelector("[data-sa-pe-grid]");
    if (!rail || rail.dataset.phase8FocusBound === "1") return;
    rail.dataset.phase8FocusBound = "1";

    let raf = null;
    const update = function () {
      raf = null;
      const cards = Array.from(rail.querySelectorAll(".sa-pe8-card"));
      if (!cards.length) return;

      const railRect = rail.getBoundingClientRect();
      const center = railRect.left + railRect.width * 0.48;

      let active = null;
      let best = Infinity;

      cards.forEach((card) => {
        const r = card.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const dist = Math.abs(c - center);
        if (dist < best) {
          best = dist;
          active = card;
        }
      });

      cards.forEach((card) => {
        const isActive = card === active;
        card.classList.toggle("is-active-mobile", isActive);
      });
    };

    rail.addEventListener("scroll", function () {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });

    window.addEventListener("resize", function () {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });

    update();
  }

  function bindPhase8CinematicScroll(section) {
    if (!section || section.dataset.phase8ScrollBound === "1") return;
    section.dataset.phase8ScrollBound = "1";

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = null;
    const update = function () {
      raf = null;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
      section.style.setProperty("--section-progress", progress.toFixed(3));
    };

    window.addEventListener("scroll", function () {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });

    update();
  }

  function applyPhase8Luxury(section) {
    bindPhase8RailFocus(section);
    bindPhase8CinematicScroll(section);
  }




  function applyPhase10CinematicPairing(section, data) {
    const pairing = data?.cinematic_pairing || data?.phase10_narrative?.cinematic_pairing || [];
    if (!Array.isArray(pairing) || !pairing.length) return;

    const cards = Array.from(section.querySelectorAll(".sa-pe8-card"));

    pairing.forEach((item, index) => {
      const card = cards[index];
      if (!card) return;

      card.setAttribute("data-sa-pe-editorial-function", item.editorial_function || "");
      const roleEl = card.querySelector("[data-sa-card-role]");
      if (roleEl && item.role_label) roleEl.textContent = item.role_label;

      const why = card.querySelector("[data-sa-card-why]");
      if (why && item.editorial_function) why.textContent = item.editorial_function;
    });
  }


  function phase9MeasureVisualWeight(section) {
    const cards = Array.from(section.querySelectorAll(".sa-pe8-card"));
    if (!cards.length) return;

    cards.forEach((card, index) => {
      const img = card.querySelector("img");
      if (!img) return;

      const title = (card.textContent || "").toLowerCase();
      let crop = "center";

      if (/bodysuit|basque|corset|teddy|harness/.test(title)) crop = "center top";
      if (/robe|chemise|nightdress|slip/.test(title)) crop = "center center";
      if (/garter|stocking|panty/.test(title)) crop = "center bottom";

      img.style.objectPosition = crop;
      card.setAttribute("data-sa-crop", crop.replace(/\s+/g, "-"));

      if (index === 0) card.setAttribute("data-sa-visual-weight", "dominant");
      else if (index === 3) card.setAttribute("data-sa-visual-weight", "finisher");
      else card.setAttribute("data-sa-visual-weight", "support");
    });
  }

  function phase9ApplySequencing(section) {
    const cards = Array.from(section.querySelectorAll(".sa-pe8-card"));
    const future = section.querySelector(".sa-pe8__future");
    const reads = section.querySelector(".sa-pe8__reads");
    const title = section.querySelector(".sa-pe8__title-row");

    cards.forEach((card, index) => {
      const role = card.getAttribute("data-sa-role") || "";
      let delay = 80 + index * 90;
      if (role === "lead_piece" || index === 0) delay = 40;
      if (role === "mood_finisher" || index === 3) delay = 330;
      card.style.setProperty("--phase9-delay", `${delay}ms`);
    });

    if (reads) reads.style.setProperty("--phase9-delay", "120ms");
    if (title) title.style.setProperty("--phase9-delay", "180ms");
    if (future) future.style.setProperty("--phase9-delay", "420ms");
  }

  function phase9CenterActiveRailCard(section) {
    const rail = section.querySelector("[data-sa-pe-grid]");
    if (!rail || rail.dataset.phase9CenterBound === "1") return;
    rail.dataset.phase9CenterBound = "1";

    let raf = null;

    const update = () => {
      raf = null;
      const cards = Array.from(rail.querySelectorAll(".sa-pe8-card"));
      if (!cards.length) return;

      const rect = rail.getBoundingClientRect();
      const center = rect.left + rect.width * 0.48;

      let active = cards[0];
      let best = Infinity;

      cards.forEach(card => {
        const r = card.getBoundingClientRect();
        const dist = Math.abs((r.left + r.width / 2) - center);
        if (dist < best) {
          best = dist;
          active = card;
        }
      });

      cards.forEach(card => {
        const r = card.getBoundingClientRect();
        const dist = Math.abs((r.left + r.width / 2) - center);
        const norm = Math.min(1, dist / Math.max(1, rect.width * .62));
        card.style.setProperty("--rail-distance", norm.toFixed(3));
        card.classList.toggle("is-active-mobile", card === active);
        card.classList.toggle("is-near-mobile", norm < .55 && card !== active);
      });
    };

    rail.addEventListener("scroll", () => {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });

    window.addEventListener("resize", () => {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });

    update();
  }

  function phase9CinematicSectionProgress(section) {
    if (!section || section.dataset.phase9ProgressBound === "1") return;
    section.dataset.phase9ProgressBound = "1";

    let raf = null;

    const update = () => {
      raf = null;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
      const centerOffset = Math.max(-1, Math.min(1, ((rect.top + rect.height / 2) - vh / 2) / vh));
      section.style.setProperty("--section-progress", progress.toFixed(3));
      section.style.setProperty("--section-center-offset", centerOffset.toFixed(3));
    };

    window.addEventListener("scroll", () => {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });

    window.addEventListener("resize", () => {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });

    update();
  }

  function phase9EditorialHover(section) {
    if (!section || section.dataset.phase9HoverBound === "1") return;
    section.dataset.phase9HoverBound = "1";

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    section.addEventListener("pointerenter", e => {
      const card = e.target.closest(".sa-pe8-card");
      if (!card) return;
      section.classList.add("sa-pe8--card-hovering");
      card.classList.add("is-hovered");
    }, true);

    section.addEventListener("pointerleave", e => {
      const card = e.target.closest(".sa-pe8-card");
      if (!card) return;
      card.classList.remove("is-hovered");
      section.classList.remove("sa-pe8--card-hovering");
    }, true);
  }

  function phase9ApplyVisualOrchestration(section) {
    phase9MeasureVisualWeight(section);
    phase9ApplySequencing(section);
    phase9CenterActiveRailCard(section);
    phase9CinematicSectionProgress(section);
    phase9EditorialHover(section);
    section.classList.add("sa-pe8--phase9");
  }


  async function run() {
    const section = document.querySelector("[data-sa-private-edit]");
    if (!section) return;

    const p = await hydrateFromRecentlyViewed();

    if (!p.views.length || p.views.length < 2) {
      section.hidden = true;
      return;
    }

    const ai = await fetchPrivateEditAI();

    if (ai && ai.version) {
      section.setAttribute("data-sa-private-edit-version", ai.version);
    }

    if (ai && ai.state) {
  section.setAttribute("data-sa-private-edit-state", String(ai.state));
}

    if (ai && typeof ai.confidence !== "undefined") {
      section.setAttribute("data-sa-private-edit-confidence", String(ai.confidence));
    }

    let hasProducts = false;

    if (ai && ai.ok && ai.products && ai.products.length) {
      updateCopyFromAI(section, ai, p);
      hasProducts = renderProductsFromAI(section, ai);
    }

    if (!hasProducts) {
      updateCopyFromProfile(section, p);
      hasProducts = renderProductsFromProfile(section, p);
    }

    if (!hasProducts) {
      section.hidden = true;
      return;
    }

    renderRecent(section, p);
    whyToggle(section);
    applyPhase6Luxury(section);
    applyPhase8Luxury(section);
    if (ai) applyPhase10CinematicPairing(section, ai);
    phase9ApplyVisualOrchestration(section);

    section.hidden = false;
    requestAnimationFrame(() => {
      section.classList.add("is-ready");
      applyPhase6Luxury(section);
      applyPhase8Luxury(section);
      if (ai) applyPhase10CinematicPairing(section, ai);
      phase9ApplyVisualOrchestration(section);
    });
  }

document.addEventListener("DOMContentLoaded", function () {
    syncCustomerProfile();
    trackCurrentPdp();
    trackClicksAndSize();
    trackDwellTime();
    trackScrollDepth();
    trackHoverIntent();
    run();
  });

  document.addEventListener("shopify:section:load", run);

  window.saPrivateEditRun = run;
  window.saPrivateEditVisitorId = getVisitorId;
})();